<script setup>
// NOTICE: KAPTION-INTEGRATION — Table of individual send events for campaign reports
// Columns: Phone, Status (badge), Timestamp
// Server-side pagination: parent fetches one page at a time, this component emits
// page-change, filter-change, and export-csv events for the parent to handle.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  events: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  accountId: { type: [String, Number], default: '' },
  // NOTICE: KAPTION-INTEGRATION — server-side pagination props
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 0 },
  pageSize: { type: Number, default: 50 },
  statusFilter: { type: String, default: 'all' },
  csvExporting: { type: Boolean, default: false },
  showExport: { type: Boolean, default: true },
});

const emit = defineEmits(['pageChange', 'filterChange', 'exportCsv']);

const router = useRouter();

const { t } = useI18n();

const STATUS_BADGES = {
  sent: 'bg-n-slate-4 text-n-slate-11',
  delivered: 'bg-n-teal-4 text-n-teal-11',
  read: 'bg-n-teal-4 text-n-teal-11',
  replied: 'bg-n-green-4 text-n-green-11',
  clicked: 'bg-n-green-4 text-n-green-11',
  failed: 'bg-n-ruby-4 text-n-ruby-11',
};

const FILTER_OPTIONS = [
  'all',
  'sent',
  'delivered',
  'read',
  'replied',
  'clicked',
  'failed',
];

// NOTICE: KAPTION-INTEGRATION — normalize status values from wanalytics
// API may return "reply" instead of "replied"
const normalizeStatus = status => {
  const s = (status || 'sent').toLowerCase();
  if (s === 'reply') return 'replied';
  if (s === 'confirmed') return 'replied';
  if (s === 'click') return 'clicked';
  return s;
};

// NOTICE: KAPTION-INTEGRATION — which link was clicked (from the click event's
// original_payload: link_slot minted into the slug metadata at shorten time).
const linkInfo = event => {
  if (normalizeStatus(event.status || event.event_type) !== 'clicked')
    return null;
  let payload = event.original_payload || event.originalPayload;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = null;
    }
  }
  const slot = payload?.link_slot || event.link_slot || null;
  const destination = payload?.destination || event.destination || '';
  if (!slot && !destination) return null;
  return { slot, destination };
};

const linkSlotLabel = slot => {
  if (!slot) return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.UNKNOWN_LINK');
  const btn = slot.match(/^button\[(\d+)\]$/);
  if (btn) {
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BUTTON_N', {
      n: Number(btn[1]) + 1,
    });
  }
  if (/^body\[/.test(slot))
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BODY_LINK');
  return slot;
};

const statusLabel = status => {
  const key = `CAMPAIGN.REPORT.TABLE.STATUS.${normalizeStatus(status).toUpperCase()}`;
  return t(key);
};

const badgeClass = status =>
  STATUS_BADGES[normalizeStatus(status)] || STATUS_BADGES.sent;

const formatTimestamp = ts => {
  if (!ts) return '-';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getPhone = event =>
  event.phone || event.phone_number || event.recipient || '-';

// NOTICE: KAPTION-INTEGRATION — extract contact_id for navigation to contact page
const getContactId = event => {
  const cid = event.contact_id || event.contactId;
  return cid ? Number(cid) : null;
};

const navigateToContact = event => {
  const contactId = getContactId(event);
  if (contactId && props.accountId) {
    router.push({
      name: 'contacts_edit',
      params: { contactId, accountId: props.accountId },
    });
  }
};

// NOTICE: KAPTION-INTEGRATION — server-side pagination computeds
const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.totalItems / props.pageSize))
);

const rangeStart = computed(() => (props.currentPage - 1) * props.pageSize + 1);
const rangeEnd = computed(() =>
  Math.min(props.currentPage * props.pageSize, props.totalItems)
);

const hasEvents = computed(
  () => props.events.length > 0 || props.totalItems > 0
);

const setFilter = value => {
  emit('filterChange', value);
};

const prevPage = () => {
  if (props.currentPage > 1) emit('pageChange', props.currentPage - 1);
};

const nextPage = () => {
  if (props.currentPage < totalPages.value)
    emit('pageChange', props.currentPage + 1);
};

const exportCSV = () => {
  if (!props.showExport) return;
  emit('exportCsv');
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Header row -->
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('DIRECT.REPORT.RECENT_SENDS') }}
      </h3>
      <button
        v-if="hasEvents && showExport"
        type="button"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-black4 transition-colors disabled:opacity-40"
        :disabled="csvExporting"
        @click="exportCSV"
      >
        <Spinner v-if="csvExporting" class="w-3.5 h-3.5" />
        <span v-else class="i-lucide-download w-3.5 h-3.5" />
        {{ t('CAMPAIGN.REPORT.FILTERS.DOWNLOAD_CSV') }}
      </button>
    </div>

    <!-- Status filter pills -->
    <div v-if="hasEvents" class="flex items-center gap-1.5 flex-wrap">
      <button
        v-for="opt in FILTER_OPTIONS"
        :key="opt"
        type="button"
        class="px-2.5 py-1 text-xs font-medium rounded-full border transition-colors"
        :class="
          statusFilter === opt
            ? 'bg-n-slate-12 text-n-solid-2 border-n-slate-12'
            : 'bg-transparent text-n-slate-11 border-n-weak hover:bg-n-alpha-black4'
        "
        @click="setFilter(opt)"
      >
        {{
          opt === 'all' ? t('CAMPAIGN.REPORT.FILTERS.ALL') : statusLabel(opt)
        }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!hasEvents"
      class="flex flex-col items-center justify-center py-12 gap-2"
    >
      <span class="i-lucide-inbox w-8 h-8 text-n-slate-8" />
      <span class="text-sm text-n-slate-10">
        {{ t('DIRECT.REPORT.NO_EVENTS') }}
      </span>
    </div>

    <!-- No results for filter -->
    <div
      v-else-if="events.length === 0 && !loading"
      class="flex flex-col items-center justify-center py-12 gap-2"
    >
      <span class="i-lucide-filter-x w-8 h-8 text-n-slate-8" />
      <span class="text-sm text-n-slate-10">
        {{ t('CAMPAIGN.REPORT.FILTERS.NO_RESULTS') }}
      </span>
    </div>

    <!-- Table + pagination -->
    <template v-else>
      <div class="rounded-xl border border-n-weak overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-black2">
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('DIRECT.REPORT.PHONE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('DIRECT.REPORT.STATUS') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('DIRECT.REPORT.TIMESTAMP') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="(event, idx) in events"
              :key="idx"
              class="hover:bg-n-alpha-black2 transition-colors"
            >
              <td class="px-4 py-3 font-mono text-xs">
                <span class="inline-flex items-center gap-1.5">
                  <button
                    v-if="getContactId(event)"
                    type="button"
                    class="text-n-blue-11 hover:underline cursor-pointer"
                    @click="navigateToContact(event)"
                  >
                    {{ getPhone(event) }}
                  </button>
                  <span v-else class="text-n-slate-12">
                    {{ getPhone(event) }}
                  </span>
                  <!-- NOTICE: KAPTION-INTEGRATION — which-link tooltip on click rows -->
                  <span
                    v-if="linkInfo(event)"
                    class="relative group inline-grid place-items-center w-5 h-5 rounded-md bg-n-green-4 text-n-green-11 cursor-help"
                  >
                    <span class="i-lucide-link w-3 h-3" />
                    <span
                      class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-30 hidden group-hover:block whitespace-nowrap rounded-lg border border-n-weak bg-n-solid-1 shadow-xl px-2.5 py-1.5 text-left font-sans"
                    >
                      <span class="block text-xs font-semibold text-n-slate-12">
                        {{ linkSlotLabel(linkInfo(event).slot) }}
                      </span>
                      <span
                        v-if="linkInfo(event).destination"
                        class="block text-[11px] text-n-slate-10"
                      >
                        {{ linkInfo(event).destination }}
                      </span>
                    </span>
                  </span>
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="badgeClass(event.status)"
                >
                  {{ statusLabel(event.status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-10 text-xs">
                {{ formatTimestamp(event.timestamp || event.sent_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex items-center justify-between pt-1">
        <span class="text-xs text-n-slate-10">
          {{ rangeStart }}-{{ rangeEnd }} / {{ totalItems }}
        </span>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-black4 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            :disabled="currentPage <= 1"
            @click="prevPage"
          >
            <span class="i-lucide-chevron-left w-4 h-4" />
          </button>
          <span class="text-xs text-n-slate-11 px-2">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-black4 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            :disabled="currentPage >= totalPages"
            @click="nextPage"
          >
            <span class="i-lucide-chevron-right w-4 h-4" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
